import React, { useState } from 'react';
import { Zap, Loader2, CheckCircle, ArrowRight, Database, Sparkles } from 'lucide-react';
import { seedAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const STEPS = [
  {
    icon: Zap,
    title: 'Welcome to EnergyOS ERP 360',
    desc: 'Your AI-powered energy & renewables management platform. Let\'s get you set up in 60 seconds.',
    action: null,
  },
  {
    icon: Database,
    title: 'Load Demo Data',
    desc: 'Populate the platform with realistic sample data — 7 assets (solar, wind, hydro, battery, grid), 30 days of production logs, PPA contracts, regulatory filings, ESG metrics, and maintenance work orders. Perfect for exploring all features.',
    action: 'demo',
  },
  {
    icon: Sparkles,
    title: 'You\'re ready',
    desc: 'Your dashboard is now populated. Explore the modules, test the AI assistant, and configure your settings. Remove demo data anytime from Settings.',
    action: null,
  },
];

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep]     = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleDemo = async () => {
    setLoading(true);
    try {
      await seedAPI.loadDemo();
      toast.success('Demo data loaded — 7 assets, PPAs, filings and more!');
      setStep(2);
    } catch {
      toast.error('Failed to load demo data');
    } finally { setLoading(false); }
  };

  const handleNext = () => {
    if (step === 0) { setStep(1); return; }
    if (step === 1) { handleDemo(); return; }
    if (step === 2) { onComplete(); navigate('/dashboard'); }
  };

  const handleSkip = () => {
    if (step === 1) { setStep(2); return; }
    onComplete();
  };

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-sidebar/95 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-primary-500 transition-all duration-500" style={{ width: `${((step+1)/STEPS.length)*100}%` }}/>
        </div>

        <div className="p-8">
          {/* Icon */}
          <div className="h-16 w-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-6 mx-auto">
            <Icon size={28} className="text-primary-500"/>
          </div>

          {/* Content */}
          <h2 className="text-xl font-bold text-gray-900 text-center mb-3">{current.title}</h2>
          <p className="text-gray-500 text-center text-sm leading-relaxed mb-8">{current.desc}</p>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i === step ? 'w-8 bg-primary-500' : i < step ? 'w-2 bg-primary-300' : 'w-2 bg-gray-200'}`}/>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {step === 1 && (
              <button onClick={handleSkip} className="flex-1 py-2.5 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                Skip — I'll add my own data
              </button>
            )}
            {step === 2 && (
              <button onClick={handleSkip} className="flex-1 py-2.5 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                Close
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 disabled:opacity-60 transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin"/> : null}
              {step === 0 ? 'Get Started' : step === 1 ? (loading ? 'Loading data…' : 'Load Demo Data') : 'Go to Dashboard'}
              {!loading && <ArrowRight size={15}/>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
